import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShoppingCart, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Spinner, Empty } from "@/components/common";
import { useApi, sar } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const ProductCard = ({ p }) => {
  const cart = useCart();
  const [inst, setInst] = useState(p.price === 0);
  const add = () => {
    cart.add(p, inst);
    toast.success("أضيف إلى السلة");
  };
  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-[#E5DEC9] bg-white" data-testid={`product-card-${p.id}`}>
      <div className="overflow-hidden"><img src={p.image || "/images/bulb1.jpg"} alt={p.name} className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
      <div className="flex flex-1 flex-col p-5">
        <span className="text-xs font-semibold text-[#C59B27]">{p.category}</span>
        <h3 className="mt-1 font-bold text-[#0F382C]">{p.name}</h3>
        <p className="mt-1 flex-1 text-sm text-[#5C7067]">{p.description}</p>
        <div className="mt-4 flex items-end justify-between">
          <div>
            <p className="text-xl font-extrabold text-[#0F382C]" data-testid={`product-price-${p.id}`}>{p.price === 0 ? "خدمة" : sar(p.price)}</p>
            <p className="text-xs text-[#5C7067]" data-testid={`product-install-fee-${p.id}`}>التركيب: {sar(p.installation_fee)}</p>
          </div>
        </div>
        <label className="mt-4 flex items-center justify-between rounded-lg bg-[#F7F4EF] px-3 py-2 text-sm">
          <span className="flex items-center gap-2"><Wrench className="h-4 w-4 text-[#0F382C]" />مع التركيب</span>
          <Switch checked={inst} disabled={p.price === 0} onCheckedChange={setInst} data-testid={`product-install-toggle-${p.id}`} />
        </label>
        <Button onClick={add} className="mt-3 rounded-full bg-[#0F382C] hover:bg-[#164E3D]" data-testid={`add-to-cart-${p.id}`}>
          <ShoppingCart className="ml-2 h-4 w-4" />أضف للسلة
        </Button>
      </div>
    </div>
  );
};

export default function Store() {
  const api = useApi();
  const cart = useCart();
  const [products, setProducts] = useState(null);
  const [cat, setCat] = useState("الكل");
  useEffect(() => {
    api("get", "/products").then(setProducts).catch(() => setProducts([]));
  }, [api]);
  const cats = useMemo(() => ["الكل", ...new Set((products || []).map((p) => p.category))], [products]);
  const shown = (products || []).filter((p) => cat === "الكل" || p.category === cat);

  return (
    <div data-testid="store-page">
      <PageHeader title="الخدمات الشخصية" subtitle="منتجات وخدمات لوحدتك مع خيار التركيب — الدفع عند التنفيذ">
        <Button variant="outline" onClick={() => cart.setOpen(true)} className="rounded-full border-[#0F382C] text-[#0F382C]" data-testid="store-open-cart-button">
          <ShoppingCart className="ml-2 h-4 w-4" />السلة ({cart.totals.count})
        </Button>
      </PageHeader>
      <div className="mb-8 flex gap-2 overflow-x-auto">
        {cats.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`store-category-${c}`}
            className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-semibold transition-colors ${cat === c ? "bg-[#0F382C] text-white" : "bg-white border border-[#E5DEC9] text-[#0F382C] hover:border-[#0F382C]"}`}>
            {c}
          </button>
        ))}
      </div>
      {products === null ? <Spinner /> : shown.length === 0 ? <Empty title="لا توجد منتجات حالياً" testId="store-empty" /> : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{shown.map((p) => <ProductCard key={p.id} p={p} />)}</div>
      )}
    </div>
  );
}
